import { doc, onSnapshot } from 'firebase/firestore';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/Button';
import { useOrg } from '@/contexts/OrgContext';
import { listCatalog } from '@finvox/connectors';
import { apiPost } from '@/lib/api';
import { getFirestoreDb } from '@/lib/firebase';
import { isWorkerFirebaseReady } from '@/lib/workerApi';
import { useToast } from '@/contexts/ToastContext';
import { toastError } from '@/lib/toastErrors';
import { useOnlineStatus } from '@/hooks/useOnlineStatus';

type CatalogEntry = { id: string; nameKey: string; descriptionKey: string };

type ConnectorConfig = {
  enabled: boolean;
  status?: string;
  config?: Record<string, string>;
  lastSyncAt?: unknown;
  lastError?: string;
};

export function ConnectorMarketplace() {
  const { t } = useTranslation();
  const { orgId, currentPeriodId, skipLiveUpdate } = useOrg();
  const online = useOnlineStatus();
  const toast = useToast();

  const [catalog, setCatalog] = useState<CatalogEntry[]>([]);
  const [config, setConfig] = useState<ConnectorConfig | null>(null);
  const [projectId, setProjectId] = useState('');
  const [storeId, setStoreId] = useState('default');
  const [accountCash, setAccountCash] = useState('1000');
  const [accountRevenue, setAccountRevenue] = useState('4000');
  const [accountTax, setAccountTax] = useState('2100');
  const [credentialsJson, setCredentialsJson] = useState('');
  const [syncJobId, setSyncJobId] = useState<string | null>(null);
  const [syncProgress, setSyncProgress] = useState('');
  const [taxNames, setTaxNames] = useState<string[]>([]);
  const [taxMapping, setTaxMapping] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setCatalog(
      listCatalog().map((c) => ({
        id: c.id,
        nameKey: c.nameKey,
        descriptionKey: c.descriptionKey
      }))
    );
  }, []);

  async function requireWorker(): Promise<boolean> {
    if (!(await isWorkerFirebaseReady())) {
      toast.error(t('errors.workerUnavailable'), 'connectors');
      return false;
    }
    return true;
  }

  useEffect(() => {
    if (!orgId) return;
    return onSnapshot(doc(getFirestoreDb(), 'organizations', orgId, 'connectorConfigs', 'payvox'), (snap) => {
      if (skipLiveUpdate(snap.metadata, orgId)) return;
      const d = snap.data();
      if (!d) {
        setConfig(null);
        return;
      }
      setConfig({
        enabled: Boolean(d.enabled),
        status: String(d.status ?? ''),
        config: (d.config ?? {}) as Record<string, string>,
        lastSyncAt: d.lastSyncAt,
        lastError: d.lastError ? String(d.lastError) : undefined
      });
      const c = d.config as Record<string, string> | undefined;
      if (c?.projectId) setProjectId(c.projectId);
      if (c?.storeId) setStoreId(c.storeId);
      if (c?.accountCash) setAccountCash(c.accountCash);
      if (c?.accountRevenue) setAccountRevenue(c.accountRevenue);
      if (c?.accountTax) setAccountTax(c.accountTax);
    });
  }, [orgId, skipLiveUpdate]);

  async function saveConfig(enabled: boolean) {
    if (!orgId || !online || !(await requireWorker())) return;
    await apiPost('/v1/connectors/config', {
      orgId,
      connectorId: 'payvox',
      enabled,
      config: { projectId, storeId, accountCash, accountRevenue, accountTax }
    });
  }

  async function saveCredentials() {
    if (!orgId || !online || !credentialsJson.trim() || !(await requireWorker())) return;
    setBusy(true);
    try {
      JSON.parse(credentialsJson);
      await apiPost('/v1/connectors/credentials', {
        orgId,
        connectorId: 'payvox',
        serviceAccountJson: credentialsJson.trim()
      });
      setCredentialsJson('');
      toast.success(t('connectors.credentialsSaved'), 'connectors');
    } catch (e) {
      toastError(toast, e, 'connectors', 'connectors.invalidJson');
    } finally {
      setBusy(false);
    }
  }

  async function testConnection() {
    if (!orgId || !online || !(await requireWorker())) return;
    setBusy(true);
    try {
      const res = await apiPost<{ ok: boolean; message?: string }>('/v1/connectors/test', {
        orgId,
        connectorId: 'payvox'
      });
      if (res.ok) toast.success(t('connectors.testOk'), 'connectors');
      else toast.error(res.message ?? t('connectors.testFailed'), 'connectors');
    } catch (e) {
      toastError(toast, e, 'connectors', 'connectors.testFailed');
    } finally {
      setBusy(false);
    }
  }

  async function syncAll() {
    if (!orgId || !online || !(await requireWorker())) return;
    setBusy(true);
    let currentJobId: string | undefined;
    let cursor: string | null = null;
    let totalStaged = 0;

    try {
      type SyncPage = {
        jobId: string;
        done: boolean;
        nextCursor: string | null;
        staged: number;
      };
      let done = false;
      while (!done) {
        const page: SyncPage = await apiPost<SyncPage>('/v1/connectors/sync', {
          orgId,
          connectorId: 'payvox',
          jobId: currentJobId,
          cursor
        });
        currentJobId = page.jobId;
        cursor = page.nextCursor;
        totalStaged += page.staged;
        setSyncJobId(page.jobId);
        setSyncProgress(t('connectors.syncProgress', { count: totalStaged }));
        done = page.done;
      }
      toast.success(t('connectors.syncDone', { count: totalStaged }), 'connectors');
      if (currentJobId) {
        const tax = await apiPost<{ names: string[] }>('/v1/connectors/staging/tax-names', {
          orgId,
          jobId: currentJobId
        });
        setTaxNames(tax.names);
      }
    } catch (e) {
      toastError(toast, e, 'connectors', 'connectors.syncFailed');
    } finally {
      setBusy(false);
    }
  }

  async function commitJournals() {
    if (!orgId || !syncJobId || !currentPeriodId || !online || !(await requireWorker())) return;
    setBusy(true);
    let cursor = 0;
    let processed = 0;
    try {
      for (;;) {
        const res = await apiPost<{
          done: boolean;
          cursor: number;
          processed: number;
          skipped: number;
          failed: number;
        }>('/v1/connectors/staging/commit-journals', {
          orgId,
          jobId: syncJobId,
          periodId: currentPeriodId,
          cursor
        });
        processed += res.processed;
        cursor = res.cursor;
        if (res.done) {
          toast.success(
            t('import.journalsCommitted', {
              processed,
              skipped: res.skipped,
              failed: res.failed
            }),
            'connectors'
          );
          break;
        }
      }
    } catch (e) {
      toastError(toast, e, 'connectors', 'connectors.commitFailed');
    } finally {
      setBusy(false);
    }
  }

  const payvox = catalog.find((c) => c.id === 'payvox');

  return (
    <div className="rounded-lg border border-[var(--fv-border)] p-4">
      <h2 className="text-lg font-semibold">{t('connectors.title')}</h2>
      <p className="mt-1 text-sm text-[var(--fv-text-muted)]">{t('connectors.subtitle')}</p>

      {payvox && (
        <div className="mt-6 space-y-4">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h3 className="font-medium">{t(payvox.nameKey)}</h3>
              <p className="text-sm text-[var(--fv-text-muted)]">{t(payvox.descriptionKey)}</p>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={config?.enabled ?? false}
                onChange={(e) => void saveConfig(e.target.checked)}
                disabled={!online}
              />
              {t('connectors.enabled')}
            </label>
          </div>

          {config?.status && (
            <p className="text-sm">
              {t('connectors.status')}: <span className="font-medium">{config.status}</span>
              {config.lastError && (
                <span className="ms-2 text-red-600">{config.lastError}</span>
              )}
            </p>
          )}

          <div className="grid gap-3 md:grid-cols-2">
            <label className="text-sm">
              {t('connectors.projectId')}
              <input
                className="mt-1 w-full rounded border border-[var(--fv-border)] bg-[var(--fv-surface)] px-3 py-2"
                value={projectId}
                onChange={(e) => setProjectId(e.target.value)}
              />
            </label>
            <label className="text-sm">
              {t('connectors.storeId')}
              <input
                className="mt-1 w-full rounded border border-[var(--fv-border)] bg-[var(--fv-surface)] px-3 py-2"
                value={storeId}
                onChange={(e) => setStoreId(e.target.value)}
              />
            </label>
            <label className="text-sm">
              {t('connectors.accountCash')}
              <input
                className="mt-1 w-full rounded border border-[var(--fv-border)] bg-[var(--fv-surface)] px-3 py-2"
                value={accountCash}
                onChange={(e) => setAccountCash(e.target.value)}
              />
            </label>
            <label className="text-sm">
              {t('connectors.accountRevenue')}
              <input
                className="mt-1 w-full rounded border border-[var(--fv-border)] bg-[var(--fv-surface)] px-3 py-2"
                value={accountRevenue}
                onChange={(e) => setAccountRevenue(e.target.value)}
              />
            </label>
            <label className="text-sm">
              {t('connectors.accountTax')}
              <input
                className="mt-1 w-full rounded border border-[var(--fv-border)] bg-[var(--fv-surface)] px-3 py-2"
                value={accountTax}
                onChange={(e) => setAccountTax(e.target.value)}
              />
            </label>
          </div>

          <Button variant="secondary" onClick={() => void saveConfig(config?.enabled ?? true)} disabled={!online}>
            {t('connectors.saveConfig')}
          </Button>

          <label className="block text-sm">
            {t('connectors.serviceAccount')}
            <textarea
              className="mt-1 h-24 w-full rounded border border-[var(--fv-border)] bg-[var(--fv-surface)] px-3 py-2 font-mono text-xs"
              placeholder="{ ... service account JSON ... }"
              value={credentialsJson}
              onChange={(e) => setCredentialsJson(e.target.value)}
            />
          </label>
          <p className="text-xs text-[var(--fv-text-muted)]">{t('connectors.credentialsHint')}</p>

          <div className="flex flex-wrap gap-2">
            <Button variant="secondary" onClick={() => void saveCredentials()} disabled={busy || !online}>
              {t('connectors.saveCredentials')}
            </Button>
            <Button variant="secondary" onClick={() => void testConnection()} disabled={busy || !online}>
              {t('connectors.test')}
            </Button>
            <Button onClick={() => void syncAll()} disabled={busy || !online || !config?.enabled}>
              {busy ? t('connectors.syncing') : t('connectors.syncAll')}
            </Button>
          </div>

          {syncProgress && <p className="text-sm text-[var(--fv-text-muted)]">{syncProgress}</p>}

          {taxNames.length > 0 && (
            <div className="rounded border border-[var(--fv-border)] p-3">
              <h4 className="text-sm font-semibold">{t('connectors.taxMapping')}</h4>
              {taxNames.map((name) => (
                <div key={name} className="mt-2 flex flex-wrap items-center gap-2 text-sm">
                  <span className="min-w-32">{name}</span>
                  <span>→</span>
                  <input
                    className="flex-1 rounded border border-[var(--fv-border)] px-2 py-1"
                    placeholder={t('connectors.finVoxTaxName')}
                    value={taxMapping[name] ?? ''}
                    onChange={(e) =>
                      setTaxMapping((m) => ({ ...m, [name]: e.target.value }))
                    }
                  />
                </div>
              ))}
            </div>
          )}

          {syncJobId && (
            <Button onClick={() => void commitJournals()} disabled={busy || !online || !currentPeriodId}>
              {t('connectors.commitJournals')}
            </Button>
          )}
        </div>
      )}

    </div>
  );
}
